import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { cartApi } from '@/lib/api';
import { useCart } from '@/lib/cart-store';
import { useLocale, useLocalePath } from '@/hooks/useLocalePath';
import { Price } from './Price';
import { formatPrice } from '@rozman/shared';
import type { JSX } from 'react';

/** Spring-physics drawer, with the per-supplier shipping breakdown shown here, not only at checkout. */
export function CartDrawer(): JSX.Element {
  const { t } = useTranslation();
  const locale = useLocale();
  const path = useLocalePath();
  const { items, drawerOpen, closeDrawer, setQuantity, remove, promoCode, setPromoCode } = useCart();
  const [codeDraft, setCodeDraft] = useState(promoCode ?? '');

  const { data: quote, isLoading } = useQuery({
    queryKey: ['cart', items, promoCode, locale],
    queryFn: () => cartApi.quote(items.map((i) => ({ variantId: i.variantId, quantity: i.quantity })), locale, promoCode),
    enabled: drawerOpen && items.length > 0,
  });

  return (
    <AnimatePresence>
      {drawerOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-50 bg-[var(--color-ink)]/40"
            onClick={closeDrawer}
            aria-hidden="true"
          />
          <motion.aside
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', stiffness: 320, damping: 34 }}
            role="dialog"
            aria-label={t('cart.title')}
            data-testid="cart-drawer"
            className="fixed right-0 top-0 z-50 flex h-full w-full max-w-md flex-col bg-[var(--color-ground-lift)]"
          >
            <div className="flex items-center justify-between p-5">
              <h2 className="font-display text-lg">{t('cart.title')}</h2>
              <button type="button" onClick={closeDrawer} className="text-xs font-semibold">{t('common.close')}</button>
            </div>

            {items.length === 0 ? (
              <div className="flex flex-1 flex-col items-center justify-center gap-4 p-8 text-center">
                <p className="text-sm text-[var(--color-muted)]">{t('cart.empty')}</p>
                <Link to={path('/kategoria/narozniki')} onClick={closeDrawer} className="rz-button">
                  {t('cart.emptyCta')}
                </Link>
              </div>
            ) : (
              <>
                <ul className="flex-1 overflow-auto px-5 space-y-4">
                  {items.map((item) => {
                    const line = quote?.lines.find((l) => l.variantId === item.variantId);
                    return (
                      <li key={item.variantId} className="flex gap-3">
                        {item.snapshot.image && (
                          <img src={item.snapshot.image} alt="" width={64} height={80} className="h-20 w-16 shrink-0 bg-[var(--color-ground-deep)] object-contain" />
                        )}
                        <div className="flex-1 min-w-0 space-y-1">
                          <p className="text-xs font-semibold leading-tight">{line?.name ?? item.snapshot.name}</p>
                          {(line?.options.length ?? 0) > 0 && (
                            <p className="text-2xs text-[var(--color-muted)]">
                              {line?.options.map((o) => `${o.label}: ${o.value}`).join(' · ')}
                            </p>
                          )}
                          <div className="flex items-center gap-2 pt-1">
                            <label className="sr-only" htmlFor={`qty-${item.variantId}`}>{t('product.quantity')}</label>
                            <input
                              id={`qty-${item.variantId}`}
                              type="number"
                              min={1}
                              max={99}
                              value={item.quantity}
                              onChange={(e) => setQuantity(item.variantId, Number(e.target.value))}
                              className="rz-field w-16 !py-1 !text-xs"
                            />
                            <button type="button" onClick={() => remove(item.variantId)} className="text-2xs text-[var(--color-muted)] underline underline-offset-2">
                              {t('cart.remove')}
                            </button>
                          </div>
                        </div>
                        <Price minor={line?.lineTotalGross ?? item.snapshot.priceGross * item.quantity} className="text-xs font-semibold shrink-0" />
                      </li>
                    );
                  })}
                </ul>

                <div className="space-y-3 p-5">
                  {quote && quote.shipments.length > 1 && (
                    <p className="text-2xs text-[var(--color-accent)] leading-relaxed">
                      {t('cart.twoShipmentsNote', { count: quote.shipments.length })}
                    </p>
                  )}
                  <form
                    className="flex gap-2"
                    onSubmit={(e) => {
                      e.preventDefault();
                      setPromoCode(codeDraft.trim().toUpperCase() || null);
                    }}
                  >
                    <label htmlFor="promo" className="sr-only">{t('cart.promoCode')}</label>
                    <input
                      id="promo" className="rz-field !py-2 !text-xs" value={codeDraft}
                      onChange={(e) => setCodeDraft(e.target.value)} placeholder={t('cart.promoCode')}
                      autoComplete="off"
                    />
                    <button type="submit" className="rz-button shrink-0 !px-4 !py-2">{t('cart.promoApply')}</button>
                  </form>
                  {promoCode && (
                    <p
                      className="text-2xs"
                      role="status"
                      style={{ color: quote?.discountGross ? 'var(--color-success)' : 'var(--color-danger)' }}
                    >
                      {quote?.discountGross
                        ? t('cart.promoApplied', { value: formatPrice(quote.discountGross, locale) })
                        : t('cart.promoInvalid')}
                    </p>
                  )}

                  <dl className="space-y-1.5 text-xs">
                    <div className="flex justify-between">
                      <dt className="text-[var(--color-muted)]">{t('cart.subtotal')}</dt>
                      <dd><Price minor={quote?.itemsTotalGross ?? null} /></dd>
                    </div>
                    {(quote?.discountGross ?? 0) > 0 && (
                      <div className="flex justify-between" style={{ color: 'var(--color-success)' }}>
                        <dt>{t('cart.promoCode')}</dt>
                        <dd>−<Price minor={quote?.discountGross ?? 0} /></dd>
                      </div>
                    )}
                    {quote?.shipments.map((s) => (
                      <div key={s.supplierCode} className="flex justify-between" data-testid="shipment-line">
                        <dt className="text-[var(--color-muted)]">{t('cart.shipmentFrom', { supplier: s.supplierName })}</dt>
                        <dd><Price minor={s.totalCost} /></dd>
                      </div>
                    ))}
                    <div className="flex justify-between pt-2 text-base font-semibold">
                      <dt>{t('cart.total')}</dt>
                      <dd><Price minor={quote?.totalGross ?? null} /></dd>
                    </div>
                  </dl>
                  <Link to={path('/zamowienie')} onClick={closeDrawer} className="rz-button w-full" data-testid="checkout-link">
                    {isLoading ? t('common.loading') : t('cart.checkout')}
                  </Link>
                </div>
              </>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
